import { useEffect, useState, useRef } from "react";

import {
    DEFAULT_PAGE_SIZE,
    MIN_PAGINATION_ROWS,
    PAGE_SIZE_OPTIONS,
    Pagination,
    RowCountLabelVariant,
} from "@impero-com/ui";
import { Table, TableProps } from "antd";

import { useTranslation } from "../../utils/i18n";

export type { ToolSpec } from "../../components/toolbar";
import * as theme from "./paginatedTable.css";

interface PaginatedTableExtraProps {
    getRowCountLabel?:
        | ((pageSize: number, totalRowCount: number) => string)
        | null;
    getSinglePageRowCountLabel?: (totalRowCount: number) => string;
    paginationVariant?: RowCountLabelVariant;
}

export function PaginatedTable<T extends Record<string | number, unknown>>({
    dataSource = [],
    getRowCountLabel = null,
    getSinglePageRowCountLabel,
    paginationVariant,
    ...restProps
}: TableProps<T> & PaginatedTableExtraProps) {
    const { t } = useTranslation();
    const showPagination = dataSource.length >= MIN_PAGINATION_ROWS;
    const tableRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const shownFrom = page * pageSize;
    const shownTo = showPagination ? (page + 1) * pageSize : dataSource.length;

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(0); // Reset to first page when page size changes
    };

    // Resets page to 0 if length of rows changes - normally due to filters
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPage(0);
    }, [dataSource.length]);

    return (
        <>
            <div>
                <Table
                    {...restProps}
                    dataSource={dataSource.slice(shownFrom, shownTo)}
                    ref={tableRef}
                />
            </div>
            <div className={theme.stickyWrapper}>
                <Pagination
                    tableElement={tableRef}
                    totalRowCount={dataSource.length}
                    pageSize={pageSize}
                    current={page}
                    onChangePage={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    pageLabel={t("Pagination_Page")}
                    getRowCountLabel={getRowCountLabel}
                    getSinglePageRowCountLabel={getSinglePageRowCountLabel}
                    variant={paginationVariant}
                />
            </div>
        </>
    );
}
